import {
  ArrowRight,
  BadgeCheck,
  Building2,
  Check,
  ChevronRight,
  CircleDollarSign,
  Coins,
  Database,
  Download,
  FileText,
  Gauge,
  Globe2,
  HandCoins,
  Landmark,
  Layers3,
  LockKeyhole,
  Menu,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  WalletCards,
  X
} from "lucide-react";
import { useMemo, useState } from "react";

type NavItem = {
  label: string;
  href: string;
};

type Product = {
  icon: JSX.Element;
  title: string;
  eyebrow: string;
  description: string;
  points: string[];
};

const navItems: NavItem[] = [
  { label: "Products", href: "#individuals" },
  { label: "Trust & Safety", href: "#security" },
  { label: "Learn", href: "#community" },
  { label: "Company", href: "#businesses" },
  { label: "Support", href: "#roadmap" }
];

const products: Product[] = [
  {
    icon: <TrendingUp aria-hidden="true" />,
    title: "Earn dashboard",
    eyebrow: "Portfolio yield preview",
    description:
      "Model digital asset reward programs with transparent tiers, disclosures, and eligibility controls before a user ever commits funds.",
    points: ["Reward rate tables", "Eligibility copy", "Consent checkpoints"]
  },
  {
    icon: <HandCoins aria-hidden="true" />,
    title: "Borrow flow",
    eyebrow: "Collateral workflow",
    description:
      "Prototype loan education, collateral views, repayment terms, and risk warnings in one clean product journey.",
    points: ["Loan calculator", "LTV education", "Repayment timeline"]
  },
  {
    icon: <Coins aria-hidden="true" />,
    title: "Swap experience",
    eyebrow: "Conversion UX",
    description:
      "Create a guided swap interface with quote previews, settlement states, and compliance-friendly confirmation screens.",
    points: ["Quote preview", "Risk confirmation", "Transaction states"]
  },
  {
    icon: <Building2 aria-hidden="true" />,
    title: "Institution portal",
    eyebrow: "Business workflows",
    description:
      "Separate institutional onboarding, treasury reporting, documentation, and sales routes from consumer acquisition.",
    points: ["Treasury overview", "Document center", "Sales qualification"]
  }
];

const routeInventory = [
  "Home",
  "Earn",
  "Borrow",
  "Swap",
  "Buy",
  "Institutional",
  "Community proof",
  "Customer care",
  "Legal",
  "Roadmap"
];

const milestones = [
  {
    label: "Archive baseline",
    date: "2022-05-11",
    detail: "Used for site structure, product page depth, legal inventory, and support taxonomy."
  },
  {
    label: "Hero reference",
    date: "2022-06-15",
    detail: "Used only for visual rhythm: large headline, phone mockup, floating asset motif, and email CTA."
  },
  {
    label: "Operational cutoff",
    date: "2022-06-12",
    detail: "Used as a validation boundary so post-freeze product claims do not leak into the FireBank build."
  }
];

const governanceItems = [
  {
    icon: <ShieldCheck aria-hidden="true" />,
    title: "Brand-safe rebuild",
    text: "Celsius trademarks, screenshots, legal terms, and historical performance claims are not shipped in the public FireBank site."
  },
  {
    icon: <Database aria-hidden="true" />,
    title: "Migration discipline",
    text: "The current deploy is static. Optional D1 migration files are additive, isolated, and documented before any live database is attached."
  },
  {
    icon: <FileText aria-hidden="true" />,
    title: "Evidence trail",
    text: "Implementation decisions, archive policy, verification results, and deployment notes are recorded under the research log."
  }
];

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [cookiesChoice, setCookiesChoice] = useState<"accepted" | "declined" | null>(null);

  const canSubmit = useMemo(() => /\S+@\S+\.\S+/.test(email), [email]);

  function submitWaitlist(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitted(true);
  }

  return (
    <div className="site-shell">
      <header className="topbar">
        <a className="brand" href="#top" aria-label="FireBank home">
          <span className="brand-mark">F</span>
          <span>FireBank</span>
        </a>
        <nav className="desktop-nav" aria-label="Primary navigation">
          {navItems.map((item) => (
            <a key={item.href} href={item.href}>
              {item.label}
            </a>
          ))}
        </nav>
        <div className="topbar-actions">
          <a className="ghost-link" href="#roadmap">
            Roadmap
          </a>
          <a className="primary-small" href="#waitlist">
            Join preview
          </a>
          <button
            className="icon-button menu-button"
            type="button"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen((value) => !value)}
          >
            {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          </button>
        </div>
        {menuOpen && (
          <nav className="mobile-nav" aria-label="Mobile navigation">
            {navItems.map((item) => (
              <a key={item.href} href={item.href} onClick={() => setMenuOpen(false)}>
                {item.label}
              </a>
            ))}
          </nav>
        )}
      </header>

      <main id="top">
        <section className="hero-section" aria-labelledby="hero-title">
          <div className="hero-copy">
            <p className="eyebrow">
              <Sparkles aria-hidden="true" />
              Public preview for a digital asset home base
            </p>
            <h1 id="hero-title">
              Your safer digital asset
            </h1>
            <p className="hero-lede">
              FireBank combines a polished crypto banking interface with clear education, visible risk checkpoints,
              and a deployment-ready Cloudflare architecture.
            </p>
            <form className="waitlist-form" id="waitlist" onSubmit={submitWaitlist}>
              <label className="sr-only" htmlFor="email">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setSubmitted(false);
                }}
              />
              <button type="submit" disabled={!canSubmit}>
                {submitted ? "You're on the list" : "Request preview"}
              </button>
            </form>
            <p className="form-note">
              Preview signups are non-custodial. FireBank does not accept deposits, execute trades, or offer lending
              products on this site.
            </p>
          </div>
          <div className="hero-visual" aria-label="FireBank app preview">
            <div className="orbital-grid" aria-hidden="true" />
            <div className="token token-pink" aria-hidden="true">
              F
            </div>
            <div className="token token-blue" aria-hidden="true">
              $
            </div>
            <div className="token token-gold" aria-hidden="true">
              B
            </div>
            <div className="phone-frame">
              <div className="phone-top">
                <span />
                <strong>Welcome Maya</strong>
              </div>
              <div className="balance-card">
                <span>Portfolio preview</span>
                <strong>$248,920.40</strong>
                <small>Demo balance</small>
              </div>
              <div className="metric-row">
                <span>Rewards model</span>
                <strong>$1,842.10</strong>
              </div>
              <div className="asset-list">
                {["Bitcoin", "USD Coin", "Ethereum", "Fire token"].map((asset, index) => (
                  <div className="asset-row" key={asset}>
                    <span className={`asset-dot asset-${index}`} />
                    <div>
                      <strong>{asset}</strong>
                      <small>{index === 3 ? "Preview asset" : "Portfolio asset"}</small>
                    </div>
                    <span>{["42.1%", "31.4%", "18.7%", "7.8%"][index]}</span>
                  </div>
                ))}
              </div>
              <div className="phone-tabs">
                <span>Portfolio</span>
                <span>Transfer</span>
                <span>Borrow</span>
              </div>
            </div>
          </div>
        </section>

        <section className="trust-strip" aria-label="FireBank operating principles">
          <div>
            <BadgeCheck aria-hidden="true" />
            <span>Brand-safe rebuild</span>
          </div>
          <div>
            <LockKeyhole aria-hidden="true" />
            <span>Risk-first UX</span>
          </div>
          <div>
            <Globe2 aria-hidden="true" />
            <span>Cloudflare-ready</span>
          </div>
        </section>

        <section className="section" id="individuals">
          <div className="section-heading">
            <p className="eyebrow">Product surface</p>
            <h2>Everything a user expects from a modern digital asset dashboard.</h2>
            <p>
              The page inventory follows the deeper May 2022 archive, while the public copy is rebuilt for FireBank
              with clear preview labels and safer financial language.
            </p>
          </div>
          <div className="product-grid">
            {products.map((product) => (
              <article className="product-card" key={product.title}>
                <div className="card-icon">{product.icon}</div>
                <p>{product.eyebrow}</p>
                <h3>{product.title}</h3>
                <span>{product.description}</span>
                <ul>
                  {product.points.map((point) => (
                    <li key={point}>
                      <Check aria-hidden="true" />
                      {point}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <section className="split-band" id="businesses">
          <div>
            <p className="eyebrow">Business view</p>
            <h2>Separate consumer momentum from institutional diligence.</h2>
            <p>
              Institutions need a quieter path: documentation, eligibility, treasury reporting, support ownership,
              and commercial qualification. FireBank keeps that journey visible without mixing it into consumer CTAs.
            </p>
            <a className="text-cta" href="#roadmap">
              View implementation roadmap <ChevronRight aria-hidden="true" />
            </a>
          </div>
          <div className="ops-panel" aria-label="Institutional workflow preview">
            <div className="ops-panel-header">
              <Landmark aria-hidden="true" />
              <span>Institution onboarding</span>
            </div>
            {["Entity profile", "Treasury policy", "Risk disclosures", "Support owner"].map((item, index) => (
              <div className="ops-row" key={item}>
                <span>{index + 1}</span>
                <strong>{item}</strong>
                <BadgeCheck aria-hidden="true" />
              </div>
            ))}
          </div>
        </section>

        <section className="section compact" id="community">
          <div className="section-heading">
            <p className="eyebrow">Information architecture</p>
            <h2>A deployment site with archive depth, not just a landing page.</h2>
          </div>
          <div className="route-grid">
            {routeInventory.map((route) => (
              <div className="route-pill" key={route}>
                <Layers3 aria-hidden="true" />
                {route}
              </div>
            ))}
          </div>
        </section>

        <section className="governance-band" id="security">
          <div className="section-heading inverted">
            <p className="eyebrow">Controls</p>
            <h2>Built with constraints that make the public site safer to ship.</h2>
          </div>
          <div className="governance-grid">
            {governanceItems.map((item) => (
              <article key={item.title}>
                <div className="card-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section" id="roadmap">
          <div className="section-heading">
            <p className="eyebrow">Engineering blueprint</p>
            <h2>Hybrid source policy, verified deployment path.</h2>
          </div>
          <div className="timeline">
            {milestones.map((milestone) => (
              <article key={milestone.label}>
                <span>{milestone.date}</span>
                <h3>{milestone.label}</h3>
                <p>{milestone.detail}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="final-cta">
          <div>
            <CircleDollarSign aria-hidden="true" />
            <h2>Launch FireBank as a polished public preview first.</h2>
            <p>
              The next phase can connect live waitlist storage, authentication, and product simulations after
              compliance language and database ownership are confirmed.
            </p>
          </div>
          <a className="primary-large" href="#waitlist">
            Request preview <ArrowRight aria-hidden="true" />
          </a>
        </section>
      </main>

      <footer className="footer">
        <div>
          <a className="brand" href="#top" aria-label="FireBank home">
            <span className="brand-mark">F</span>
            <span>FireBank</span>
          </a>
          <p>Public preview. No custody, deposits, trading, lending, or financial advice is provided by this site.</p>
        </div>
        <div className="footer-links">
          <a href="#security">Security</a>
          <a href="#roadmap">Roadmap</a>
          <a href="mailto:hello@firebank.example">Contact</a>
          <a href="https://github.com/pakmingc/fire">GitHub</a>
        </div>
      </footer>

      {!cookiesChoice && (
        <div className="cookie-bar" role="region" aria-label="Cookie notice">
          <p>We use essential cookies only for this preview experience.</p>
          <div>
            <button type="button" className="secondary-button" onClick={() => setCookiesChoice("declined")}>
              Decline
            </button>
            <button type="button" className="accent-button" onClick={() => setCookiesChoice("accepted")}>
              Accept
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
